import { Injectable } from '@angular/core';
import { UsersService } from '../modules/api';
import type { UserResponse, UpdateProfileRequest } from '../modules/api';
import { AuthService } from './auth.service';

export type ThemeMode = 'light' | 'dark';
export type ThemeName = 'material-theme-green' | 'material-theme-pink' | 'material-theme-wave' | 'material-theme-red';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly DEFAULT_THEME_NAME: ThemeName = 'material-theme-green';
  private readonly isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

  private getStoredTheme(): { themeName: string; mode: ThemeMode } | null {
    if (!this.isBrowser) return null;
    try {
      const raw = localStorage.getItem('user_data');
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<UserResponse>;
      const themeName = parsed.theme_name || this.DEFAULT_THEME_NAME;
      const mode: ThemeMode = parsed.dark_mode ? 'dark' : 'light';
      return { themeName, mode };
    } catch {
      return null;
    }
  }

  constructor(
    private usersApi: UsersService,
    private auth: AuthService,
  ) {}

  apply(themeName: string, mode: ThemeMode): void {
    if (typeof document === 'undefined') return;

    const targets = [document.documentElement, document.body];
    targets.forEach((target) => {
      target.classList.remove('light-theme', 'dark-theme');
      target.classList.remove('theme-green', 'theme-pink', 'theme-wave', 'theme-red');
    });

    const themeClassMap: Record<string, string> = {
      'material-theme-green': 'theme-green',
      'material-theme-pink': 'theme-pink',
      'material-theme-wave': 'theme-wave',
      'material-theme-red': 'theme-red',
    };
    const themeClass = themeClassMap[themeName] || 'theme-green';
    targets.forEach((target) => {
      target.classList.add(themeClass);
      target.classList.add(mode === 'dark' ? 'dark-theme' : 'light-theme');
    });
  }

  async initFromUserProfile(): Promise<void> {
    const storedTheme = this.getStoredTheme();
    if (storedTheme) {
      this.apply(storedTheme.themeName, storedTheme.mode);
    } else {
      this.apply(this.DEFAULT_THEME_NAME, 'light');
    }

    if (!this.auth.isLoggedIn()) return;

    try {
      const user = await this.usersApi.getCurrentUserProfileApiUsersMeGet().toPromise() as UserResponse;
      const mode: ThemeMode = user?.dark_mode ? 'dark' : 'light';
      const themeName = user?.theme_name || this.DEFAULT_THEME_NAME;
      this.apply(themeName, mode);

      if (this.isBrowser) {
        const stored = localStorage.getItem('user_data');
        if (stored) {
          try {
            const parsed = JSON.parse(stored) as UserResponse;
            localStorage.setItem('user_data', JSON.stringify({ ...parsed, ...user }));
          } catch {}
        }
      }
    } catch {}
  }

  async setTheme(themeName: string, darkMode: boolean): Promise<void> {
    this.apply(themeName || this.DEFAULT_THEME_NAME, darkMode ? 'dark' : 'light');

    if (!this.auth.isLoggedIn()) return;

    const req: UpdateProfileRequest = {
      theme_name: themeName || this.DEFAULT_THEME_NAME,
      dark_mode: darkMode,
    };

    try {
      const updated = await this.usersApi.updateProfileApiUsersMePatch(req).toPromise() as UserResponse;
      this.auth.setCurrentUser(updated);
    } catch {}
  }
}
